import React from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Typography } from '@mui/material';

import { ColumnConfig } from './ColumnConfig';
import type { ColumnDef } from './DataTable';
import { type FilterDef, FilterPanel } from './FilterPanel';
import { Pagination, type PaginationData } from './Pagination';

interface TableToolbarProps {
  summary: string;
  // Column config
  allColumns: ColumnDef[];
  visibleKeys: string[];
  onColumnsChange: (visibleKeys: string[], orderedColumns: ColumnDef[]) => void;
  // Filter config
  filters?: FilterDef[];
  filtersOpen?: boolean;
  onToggleFilters?: () => void;
  filterValues?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  onFilterClear?: () => void;
  // Pagination (compact, top-of-table)
  paginationData?: PaginationData;
  onPageChange?: (page: number) => void;
  /** Extra controls rendered inside the filter panel (e.g. DateRangeSelector) */
  filterExtra?: React.ReactNode;
}

export const TableToolbar: React.FC<TableToolbarProps> = ({
  summary,
  allColumns,
  visibleKeys,
  onColumnsChange,
  filters,
  filtersOpen,
  onToggleFilters,
  filterValues,
  onFilterChange,
  onFilterClear,
  paginationData,
  onPageChange,
  filterExtra,
}) => {
  const activeFilterCount = filterValues
    ? Object.values(filterValues).filter(v => v !== '' && v != null).length
    : 0;

  const hasFilters = Boolean(filters) || Boolean(filterExtra);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
          {summary}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {paginationData && onPageChange && (
            <Pagination data={paginationData} onPageChange={onPageChange} compact />
          )}
          <ColumnConfig
            allColumns={allColumns}
            visibleKeys={visibleKeys}
            onChange={onColumnsChange}
          />
          {hasFilters && onToggleFilters && (
            <Box
              onClick={onToggleFilters}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                px: 1.25,
                py: 0.5,
                borderRadius: '6px',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <FilterListIcon sx={{ fontSize: 16, color: activeFilterCount > 0 ? 'primary.main' : 'text.secondary' }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: activeFilterCount > 0 ? 'primary.main' : 'text.secondary',
                  fontWeight: activeFilterCount > 0 ? 600 : 400,
                }}
              >
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {filtersOpen && (
        <Box sx={{ mt: 1.5 }}>
          {filters && filterValues && onFilterChange && onFilterClear ? (
            <FilterPanel
              filters={filters}
              values={filterValues}
              onChange={onFilterChange}
              onClear={onFilterClear}
            >
              {filterExtra}
            </FilterPanel>
          ) : filterExtra ? (
            <Box
              sx={{
                p: 2,
                bgcolor: '#fff',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {filterExtra}
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};
